import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellSubtitle,
  DataTableCellTitle,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../../consts';
import { type AssortmentSkuLocation } from '../../../../redux/assortment';

const COLUMN_ID = 'size_sku' as const;

export type SizeSkuColumnId = typeof COLUMN_ID;

interface SizeSkuFields {
  sku: AssortmentSkuLocation['sku'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<SizeSkuFields>();

export const createSizeSkuColumn = <
  T extends SizeSkuFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.sku.id),
    {
      id: COLUMN_ID,
      size: 200,
      header: function SizeHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableCellContent>
            <DataTableCellTitle className="uppercase font-medium">
              {t('table-columns.size')}
            </DataTableCellTitle>
            <DataTableCellSubtitle>
              {t('table-columns.sku')}
            </DataTableCellSubtitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function SizeCell({ row }) {
            const { sku } = row.original;

            return (
              <DataTableCellContent>
                <DataTableCellTitle>{sku.size_id}</DataTableCellTitle>
                <DataTableCellSubtitle>{sku.id}</DataTableCellSubtitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
